import { TokenConsumptionBar } from './TokenConsumptionBar';
import { needShowWarning } from './utils';
import { AccountDisplay, type AccountDisplayProps } from '~/components/ui/AccountDisplay';
import type { TokenStats } from '~/lib/.server/rateLimit';
import { classNames } from '~/utils/classNames';
import { formatTokens } from '~/utils/format';

/**
 * TokenUsageCard component displays the token usage information for a user.
 * It shows all token bars one by one.
 */
export interface TokenUsageCardProps {
  tokensLeft: number;
  account?: AccountDisplayProps;
  className?: string;
  isFreeTier?: boolean;
  maxDailyTokens?: number;
  purchasedTokens?: number;
  referralTokens?: number;
  regularTokenBreakdown?: TokenStats['regularTokenBreakdown'];
  remainingDailyTokens?: number;
  specialTokens?: number;
}

export const TokenUsageCard = ({
  account,
  tokensLeft,
  purchasedTokens = 0,
  referralTokens = 0,
  specialTokens = 0,
  className,
  regularTokenBreakdown = [],
  isFreeTier,
  remainingDailyTokens,
  maxDailyTokens,
}: TokenUsageCardProps) => {
  const extraTokens = purchasedTokens + referralTokens + specialTokens;

  // calculate maximum tokens among all types for proper bar width scaling
  const allTokenValues = [extraTokens, ...regularTokenBreakdown.map((token) => token.available)];
  const maxTokens = allTokenValues.length > 0 ? Math.max(...allTokenValues) : 0;

  return (
    <div className={classNames('flex flex-col gap-5', className)}>
      <div className="flex gap-4 md:gap-2 justify-between flex-col md:flex-row md:flex-wrap">
        <div className="font-semibold order-2 md:order-1">
          <p className="text-xs text-bolt-ds-textSecondary">Current token balance</p>
          <p className="text-3xl font-semibold">{formatTokens(tokensLeft)}</p>
        </div>
        {account && (
          <AccountDisplay
            {...account}
            className="min-w-0 order-1 md:order-2"
            titleClassName="text-xs font-semibold"
            descriptionClassName="text-base font-normal"
          />
        )}
      </div>

      {regularTokenBreakdown.map((token) => (
        <TokenConsumptionBar
          key={token.expiresOn}
          tokens={token}
          warning={token.type === 'active-month' ? needShowWarning(token.available, token.used) : false}
          maxTokens={maxTokens}
          isFreeTier={isFreeTier}
        />
      ))}

      {extraTokens > 0 && <TokenConsumptionBar tokens={extraTokens} maxTokens={maxTokens} />}

      {isFreeTier && remainingDailyTokens && maxDailyTokens && (
        <span className="text-sm text-bolt-ds-textSecondary">
          <span className="font-semibold">Daily limit: </span>
          <span className="lowercase">
            {formatTokens(remainingDailyTokens)}/{formatTokens(maxDailyTokens)} tokens
          </span>
        </span>
      )}
    </div>
  );
};
